import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  XMarkIcon, 
  ChevronLeftIcon,
  ChevronRightIcon,
  ShareIcon,
  MapPinIcon,
  CalendarDaysIcon,
  ClockIcon,
  CurrencyRupeeIcon,
  TagIcon,
  UserIcon,
  StarIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowLongLeftIcon,
  ArrowLongRightIcon,
  PencilIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { Collaboration, CollaborationCollaborator, Portfolio } from '../../types';
import { useAppSelector } from '../../hooks/redux';
import { useNavigate } from 'react-router-dom';
import Avatar from '../common/Avatar';
import CollaboratorReviewSection from './CollaboratorReviewSection';
import { collaborationService, reviewsAPI, collaborationAPI, API_BASE_URL } from '../../services/api';
import toast from 'react-hot-toast';
import { getPreferredImageUrl } from '../../utils/imageUtils';
import { resolveImageUrl } from '../../utils/image';
import useBackButton from '../../hooks/useBackButton';
import { formatCurrency } from '../../utils/format';
import ShareModal from '../common/ShareModal';
import CreateCollaborationModal from './CreateCollaborationModal';
import SmartImage from '../common/SmartImage';
import PortfolioViewModalV2 from '../profile/PortfolioViewModalV2';
import ConfirmationModal from '../common/ConfirmationModal';
import GoogleDriveVideoEmbed from '../common/GoogleDriveVideoEmbed';

interface CollaborationShowcaseModalProps {
  collaboration: Collaboration;
  onClose: () => void;
  onNextCollaboration?: () => void;
  onPreviousCollaboration?: () => void;
  hasNext?: boolean;
  hasPrevious?: boolean;
  onRespond?: (action: 'accept' | 'reject', message: string) => Promise<void>;
  initialImageIndex?: number;
}

const CollaborationShowcaseModal: React.FC<CollaborationShowcaseModalProps> = ({
  collaboration,
  onClose,
  onNextCollaboration,
  onPreviousCollaboration,
  hasNext,
  hasPrevious,
  onRespond,
  initialImageIndex = 0
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(initialImageIndex);
  const [userCollaborator, setUserCollaborator] = useState<CollaborationCollaborator | null>(null);
  const [confirmationAction, setConfirmationAction] = useState<'accept' | 'reject' | null>(null);
  const [responseMessage, setResponseMessage] = useState('');
  const [responding, setResponding] = useState(false);
  const [reviews, setReviews] = useState<{[key: string]: {rating: number, comment: string}}>({});
  const [showShareModal, setShowShareModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showPortfolioModal, setShowPortfolioModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Handle Android back button / back gesture
  useBackButton(
    () => {
      if (showShareModal) {
        setShowShareModal(false);
        return true;
      }
      if (showPortfolioModal) {
        setShowPortfolioModal(false);
        return true;
      }
      if (showDeleteConfirm) {
        setShowDeleteConfirm(false);
        return true;
      }
      if (isEditing) {
        setIsEditing(false);
        return true;
      }
      onClose();
      return true;
    },
    true,
    100
  );
  
  const { user } = useAppSelector((state) => state.auth);
  const navigate = useNavigate();

  const [fullCollaboration, setFullCollaboration] = useState<Collaboration>(collaboration);
  const prevIdRef = useRef(collaboration.id);

  // Reset image index when collaboration changes
  useEffect(() => {
    if (prevIdRef.current !== collaboration.id) {
        setCurrentImageIndex(0);
        setReviews({}); // Reset reviews
        setResponseMessage('');
        setFullCollaboration(collaboration); // Reset to prop first
        prevIdRef.current = collaboration.id;
    }
    
    // Fetch full details to ensure we have all data (especially when opening from notification)
    const fetchFullDetails = async () => {
        try {
            const response = await collaborationAPI.getCollaboration(collaboration.id);
            if (response.data && response.data.data) {
                setFullCollaboration(response.data.data);
            }
        } catch (error) {
            console.error("Failed to fetch full collaboration details", error);
        }
    };
    
    fetchFullDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collaboration.id]);

  // Use images array directly as requested by user (no cover image prepended)
  const originalImages = fullCollaboration.images_signed || [];
  const mediumImages = fullCollaboration.medium_images_signed || [];
  
  const images = originalImages.map((img, idx) => 
    getPreferredImageUrl(img, mediumImages[idx], user?.image_quality_preference)
  );

  const getEmbedUrl = (urlStr: string): string => {
    try {
      const urlObj = new URL(urlStr);
      const host = urlObj.hostname.toLowerCase();
      const path = urlObj.pathname;

      if (host === 'youtu.be') {
        return `https://www.youtube.com/embed/${path.slice(1)}`;
      }
      if (host.includes('youtube.com')) {
        const v = urlObj.searchParams.get('v');
        if (v) return `https://www.youtube.com/embed/${v}`;
        if (path.startsWith('/embed/')) return `https://www.youtube.com/embed/${path.slice(7)}`;
        if (path.startsWith('/shorts/')) return `https://www.youtube.com/embed/${path.slice(8)}`;
      }

      if (host.includes('vimeo.com')) {
        const parts = path.split('/').filter(Boolean);
        if (parts.length > 0) {
          return `https://player.vimeo.com/video/${parts[parts.length - 1]}`;
        }
      }

      if (host.includes('instagram.com')) {
        const parts = path.split('/').filter(Boolean);
        for (let i = 0; i < parts.length; i++) {
          if ((parts[i] === 'reel' || parts[i] === 'p' || parts[i] === 'reels') && i + 1 < parts.length) {
            const pathType = parts[i] === 'reels' ? 'reel' : parts[i];
            return `https://www.instagram.com/${pathType}/${parts[i+1]}/embed/`;
          }
        }
      }

      if (host.includes('drive.google.com')) {
        const parts = path.split('/').filter(Boolean);
        for (let i = 0; i < parts.length; i++) {
          if (parts[i] === 'd' && i + 1 < parts.length) {
            return `https://drive.google.com/file/d/${parts[i+1]}/preview`;
          }
        }
        const id = urlObj.searchParams.get('id');
        if (id) {
          return `https://drive.google.com/file/d/${id}/preview`;
        }
      }
    } catch (e) {
      console.error('Failed to parse embed url', e);
    }
    return urlStr;
  };

  const isSameUrl = (url1: string, url2: string) => {
    const clean = (url: string) => {
      try {
        let path = url;
        if (path.includes('://')) {
          const u = new URL(path);
          path = u.pathname;
        }
        if (path.includes('uploads/')) {
          const idx = path.indexOf('uploads/');
          path = path.substring(idx);
        }
        const qIdx = path.indexOf('?');
        if (qIdx !== -1) {
          path = path.substring(0, qIdx);
        }
        return path.replace(/^\/+|\/+$/g, '');
      } catch(e) {
        return url;
      }
    };
    return clean(url1) === clean(url2);
  };


  const videos = fullCollaboration.videos || [];

  const mediaItems = React.useMemo(() => {
    const items: { type: 'image' | 'video'; src: string }[] = [];
    const imagesList = fullCollaboration.images || [];
    const imagesSignedList = fullCollaboration.images_signed || [];
    const videosList = fullCollaboration.videos || [];
    const orderList = fullCollaboration.media_order || [];

    if (orderList.length > 0) {
      orderList.forEach((mediaPathOrUrl) => {
        const isVideo = videosList.some(v => isSameUrl(v, mediaPathOrUrl));
        const isImage = imagesList.some(img => isSameUrl(img, mediaPathOrUrl));
        if (isVideo) {
          const vidUrl = videosList.find(v => isSameUrl(v, mediaPathOrUrl)) || mediaPathOrUrl;
          items.push({ type: 'video', src: vidUrl });
        } else if (isImage) {
          const imgUrl = imagesList.find(img => isSameUrl(img, mediaPathOrUrl)) || mediaPathOrUrl;
          const imgIdx = imagesList.findIndex(img => isSameUrl(img, mediaPathOrUrl));
          const signedUrl = imagesSignedList[imgIdx] || imgUrl;
          items.push({ type: 'image', src: signedUrl });
        }
      });
      // Fallback: Catch any images/videos that might not be in media_order
      imagesList.forEach((path, idx) => {
        if (!items.some(item => isSameUrl(item.src, path) || isSameUrl(item.src, imagesSignedList[idx] || ''))) {
          const signedUrl = imagesSignedList[idx] || path;
          items.push({ type: 'image', src: signedUrl });
        }
      });
      videosList.forEach((path) => {
        if (!items.some(item => isSameUrl(item.src, path))) {
          items.push({ type: 'video', src: path });
        }
      });
    } else {
      // Default: Prioritize photos (images) first, then videos!
      imagesList.forEach((img, idx) => {
        const signedUrl = imagesSignedList[idx] || img;
        items.push({ type: 'image', src: signedUrl });
      });
      videosList.forEach(v => {
        items.push({ type: 'video', src: v });
      });
    }
    return items;
  }, [fullCollaboration]);

  // Optimized thumbnails
  const thumbnails = fullCollaboration.thumbnail_images_signed && fullCollaboration.thumbnail_images_signed.length > 0
    ? (fullCollaboration.thumbnail_cover_image && !fullCollaboration.thumbnail_images_signed.includes(fullCollaboration.thumbnail_cover_image)
        ? [fullCollaboration.thumbnail_cover_image, ...fullCollaboration.thumbnail_images_signed]
        : fullCollaboration.thumbnail_images_signed)
    : images;

  const isOwner = fullCollaboration.creator_id === user?.id;
  const canDownload = isOwner || !!userCollaborator;

  const isPlatformVideo = (url: string) => {
    const u = url.toLowerCase();
    return u.includes('youtube.com') || u.includes('youtu.be') || u.includes('vimeo.com') || u.includes('instagram.com') || u.includes('drive.google.com');
  };

  const getGoogleDriveFileId = (url: string): string => {
    try {
      const urlObj = new URL(url);
      const pathParts = urlObj.pathname.split('/').filter(Boolean);
      for (let i = 0; i < pathParts.length; i++) {
        if (pathParts[i] === 'd' && i + 1 < pathParts.length) {
          return pathParts[i+1];
        }
      }
      return urlObj.searchParams.get('id') || '';
    } catch (e) {
      return '';
    }
  };

  // State for expanding insights
  const [showFullInsights, setShowFullInsights] = useState(false);

  useEffect(() => {
    const currentUser = fullCollaboration.collaborators?.find(c => c.user_id === user?.id);
    setUserCollaborator(currentUser || null);
  }, [fullCollaboration, user?.id]);

  // Sort collaborators to put current user first
  const sortedCollaborators = React.useMemo(() => {
    if (!fullCollaboration.collaborators) return [];
    
    return [...fullCollaboration.collaborators].sort((a, b) => {
      if (a.user_id === user?.id) return -1;
      if (b.user_id === user?.id) return 1;
      return 0;
    });
  }, [fullCollaboration.collaborators, user?.id]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        handlePreviousImage();
      } else if (e.key === 'ArrowRight') {
        handleNextImage();
      } else if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentImageIndex, mediaItems.length]);

  const handleNextImage = () => {
    if (mediaItems.length === 0) return;
    if (currentImageIndex < mediaItems.length - 1) {
      setCurrentImageIndex(prev => prev + 1);
    } else {
      setCurrentImageIndex(0); // Loop back to start
    }
  };

  const handlePreviousImage = () => {
    if (mediaItems.length === 0) return;
    if (currentImageIndex > 0) {
      setCurrentImageIndex(prev => prev - 1);
    } else {
      setCurrentImageIndex(mediaItems.length - 1); // Loop to end
    }
  };

  const handleImageClick = () => {
    setShowPortfolioModal(true);
  };

  // Construct viewable portfolio items for the modal
  const viewablePortfolios: Portfolio[] = (fullCollaboration.images || []).map((img, idx) => ({
    id: `collab-showcase-img-${fullCollaboration.id}-${idx}`,
    user_id: fullCollaboration.creator_id,
    title: fullCollaboration.title,
    description: fullCollaboration.project_description,
    image_url: img,
    thumbnail_image_url: fullCollaboration.thumbnail_images_signed?.[idx] || img,
    medium_image_url: fullCollaboration.medium_images_signed?.[idx] || img,
    tags: fullCollaboration.tags || [],
    likes: 0,
    views: 0,
    comments_count: 0,
    shares: 0,
    created_at: fullCollaboration.created_at,
    updated_at: fullCollaboration.updated_at,
    collaborators: fullCollaboration.collaborators?.map(c => c.user || {
      id: c.user_id || '',
      first_name: c.name.split(' ')[0],
      last_name: c.name.split(' ').slice(1).join(' '),
      user_type: 'creator', // Fallback
      profile_pic: c.profile_pic,
      is_profile_complete: true,
      completion_score: 100
    } as any) || [],
    user: fullCollaboration.creator,
    is_collaboration: true,
    collaboration_id: fullCollaboration.id
  }));

  const handleShare = async () => {
    setShowShareModal(true);
  };

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  const handleReviewChange = (collaboratorId: string, field: 'rating' | 'comment', value: any) => {
    setReviews(prev => ({
      ...prev,
      [collaboratorId]: {
        ...prev[collaboratorId],
        [field]: value
      }
    }));
  };

  const handleConfirmAction = async () => {
    if (!confirmationAction) return;
    setResponding(true);

    try {
      // 1. Submit Invite Response
      if (onRespond) {
        await onRespond(confirmationAction, responseMessage);
      } else {
        const approved = confirmationAction === 'accept';
        await collaborationService.submitApproval(collaboration.id, approved, responseMessage);
        toast.success(approved ? 'Invitation accepted!' : 'Invitation rejected');
      }

      // 2. Submit Reviews (if any)
      const reviewsList = Object.entries(reviews)
        .filter(([userId, review]) => userId && userId !== 'null' && userId !== 'undefined' && review.rating > 0)
        .map(([userId, review]) => ({
          booking_id: collaboration.id,
          reviewee_id: userId,
          rating: review.rating,
          comment: review.comment
        }));

      if (reviewsList.length > 0) {
        await reviewsAPI.createReview(reviewsList);
        toast.success('Reviews submitted successfully');
      }
      
      if (!onRespond) {
         window.location.reload();
      }
      
      setConfirmationAction(null);
      onClose();
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status');
    } finally {
      setResponding(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'open': return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'pending': return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'rejected': return 'bg-red-100 text-red-800 border-red-200';
      case 'in_progress': return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'completed': return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'cancelled': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Card Swipe Logic
  const handleCardTouchStart = (e: React.TouchEvent) => {
    // Store touch start position
    (e.currentTarget as any).touchStartX = e.touches[0].clientX;
  };

  const handleCardTouchEnd = (e: React.TouchEvent) => {
    const touchStartX = (e.currentTarget as any).touchStartX;
    if (touchStartX === undefined) return;
    
    const touchEndX = e.changedTouches[0].clientX;
    const diff = touchStartX - touchEndX;
    const threshold = 50; // Swipe threshold

    if (Math.abs(diff) > threshold) {
      if (diff > 0) {
        // Swipe Left -> Next
        onNextCollaboration?.();
      } else {
        // Swipe Right -> Previous
        onPreviousCollaboration?.();
      }
    }
    // Clean up
    delete (e.currentTarget as any).touchStartX;
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[80] flex items-center justify-center p-4 md:p-8"
        onClick={(e) => e.target === e.currentTarget && onClose()}
        onTouchStart={handleCardTouchStart}
        onTouchEnd={handleCardTouchEnd}
      >
        {/* Project Navigation Buttons (Desktop) */}
        <div className="absolute inset-x-8 top-1/2 -translate-y-1/2 flex justify-between pointer-events-none hidden md:flex z-60">
          {hasPrevious && (
            <button
              onClick={(e) => { e.stopPropagation(); onPreviousCollaboration?.(); }}
              className="pointer-events-auto p-3 rounded-full bg-white/10 hover:bg-white/20 text-white backdrop-blur-md transition-all transform hover:-translate-x-1"
              title="Previous Project"
            >
              <ArrowLongLeftIcon className="h-8 w-8" />
            </button>
          )}
          {/* Spacer if no previous button to keep next button on right */}
          {!hasPrevious && <div />} 
          
          {hasNext && (
            <button
              onClick={(e) => { e.stopPropagation(); onNextCollaboration?.(); }}
              className="pointer-events-auto p-3 rounded-full bg-white/10 hover:bg-white/20 text-white backdrop-blur-md transition-all transform hover:translate-x-1"
              title="Next Project"
            >
              <ArrowLongRightIcon className="h-8 w-8" />
            </button>
          )}
        </div>

        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white dark:bg-gray-900 w-full max-w-7xl h-[90vh] rounded-2xl overflow-hidden shadow-2xl flex flex-col md:flex-row relative z-40"
        >
          {/* LEFT PANEL: Image Showcase (65%) */}
          <div 
            className="w-full md:w-[65%] h-[40vh] md:h-full bg-gray-50 dark:bg-gray-950 relative flex flex-col"
            onTouchStart={(e) => e.stopPropagation()}
            onTouchEnd={(e) => e.stopPropagation()}
          >
            {/* Top Bar (Mobile Only) */}
            <div 
              className="absolute right-4 z-20 md:hidden"
              style={{ top: 'calc(max(var(--safe-area-inset-top, 0px), env(safe-area-inset-top, 0px)) + 1rem)' }}
            >
              <button onClick={onClose} className="p-2 bg-black/50 text-white rounded-full backdrop-blur-sm">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Main Media Area */}
            <div className="flex-1 relative flex items-center justify-center p-4 md:p-8 overflow-hidden group">
              {mediaItems.length > 0 ? (
                <>
                  {(() => {
                    const activeItem = mediaItems[currentImageIndex];
                    return (
                      <motion.div
                        key={`${collaboration.id}-${currentImageIndex}`}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.3 }}
                        className="relative w-full h-full flex items-center justify-center cursor-grab active:cursor-grabbing"
                        onClick={activeItem.type === 'image' ? handleImageClick : undefined}
                        drag="x"
                        dragConstraints={{ left: 0, right: 0 }}
                        dragElastic={1}
                        onDragEnd={(e, { offset, velocity }) => {
                          const swipe = offset.x;

                          if (swipe < -50 || velocity.x < -500) {
                            // Swipe Left (Next Item)
                            if (currentImageIndex < mediaItems.length - 1) {
                              handleNextImage();
                            }
                          } else if (swipe > 50 || velocity.x > 500) {
                            // Swipe Right (Previous Item)
                            if (currentImageIndex > 0) {
                              handlePreviousImage();
                            }
                          }
                        }}
                      >
                        {activeItem.type === 'video' ? (
                          <div className="relative w-full max-w-4xl aspect-video rounded-xl overflow-hidden shadow-lg z-10 bg-black">
                             {!isPlatformVideo(activeItem.src) ? (
                              <video
                                src={resolveImageUrl(activeItem.src)}
                                controls
                                controlsList={!canDownload ? "nodownload" : undefined}
                                className="absolute inset-0 w-full h-full object-contain"
                              />
                            ) : activeItem.src.includes('drive.google.com') ? (
                              <GoogleDriveVideoEmbed
                                src={activeItem.src}
                                title={`Video ${currentImageIndex + 1}`}
                                allowDownload={canDownload}
                              />
                            ) : (
                              <iframe
                                src={getEmbedUrl(activeItem.src)}
                                title={`Video ${currentImageIndex + 1}`}
                                frameBorder="0"
                                scrolling="no"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                                className="absolute border-0 rounded-xl w-full h-full inset-0"
                              ></iframe>
                            )}
                          </div>
                        ) : (
                          <>
                            {/* Low-res Thumbnail (Immediate Placeholder) */}
                            <img 
                                src={thumbnails[currentImageIndex] ? resolveImageUrl(thumbnails[currentImageIndex]) : undefined}
                                alt=""
                                className="absolute max-w-full max-h-full object-contain opacity-50 blur-sm transition-opacity duration-500"
                            />

                            <SmartImage
                                src={activeItem.src}
                                fallbackSrc={thumbnails[currentImageIndex] ? resolveImageUrl(thumbnails[currentImageIndex]) : undefined}
                                alt={`Image ${currentImageIndex + 1}`}
                                showOfflinePlaceholder={false}
                                className="relative z-10 max-w-full max-h-full object-contain shadow-lg rounded-lg pointer-events-none"
                            />
                          </>
                        )}
                      </motion.div>
                    );
                  })()}

                  {/* Media Navigation Arrows */}
                  {mediaItems.length > 1 && (
                    <>
                      <button
                        onClick={(e) => { e.stopPropagation(); handlePreviousImage(); }}
                        className="absolute left-4 p-2 rounded-full bg-white/80 dark:bg-gray-800/80 hover:bg-white dark:hover:bg-gray-700 text-gray-800 dark:text-white shadow-md transition-all opacity-0 group-hover:opacity-100 md:opacity-0 md:group-hover:opacity-100 z-50"
                      >
                        <ChevronLeftIcon className="h-6 w-6" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleNextImage(); }}
                        className="absolute right-4 p-2 rounded-full bg-white/80 dark:bg-gray-800/80 hover:bg-white dark:hover:bg-gray-700 text-gray-800 dark:text-white shadow-md transition-all opacity-0 group-hover:opacity-100 md:opacity-0 md:group-hover:opacity-100 z-50"
                      >
                        <ChevronRightIcon className="h-6 w-6" />
                      </button>
                    </>
                  )}
                  
                  {/* Media Counter */}
                  <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 px-3 py-1 bg-black/50 dark:bg-white/20 text-white text-xs rounded-full backdrop-blur-sm z-50">
                    {currentImageIndex + 1} / {mediaItems.length}
                  </div>
                </>
              ) : (
                <div className="text-gray-400 dark:text-gray-500 flex flex-col items-center">
                  <div className="w-16 h-16 bg-gray-200 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
                    <ShareIcon className="h-8 w-8 opacity-20" />
                  </div>
                  <p>No media available</p>
                </div>
              )}
            </div>

            {/* Thumbnail Strip */}
            {mediaItems.length > 1 && (
              <div className="h-20 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 p-2 flex items-center justify-start md:justify-center gap-2 overflow-x-auto scrollbar-hide">
                {mediaItems.map((item, idx) => {
                  const getMediaThumbnail = (mi: { type: 'image' | 'video'; src: string }) => {
                    if (mi.type === 'image') return resolveImageUrl(mi.src);
                    try {
                      const urlObj = new URL(mi.src);
                      const host = urlObj.hostname.toLowerCase();
                      const path = urlObj.pathname;
                      if (host === 'youtu.be') return `https://img.youtube.com/vi/${path.slice(1)}/hqdefault.jpg`;
                      if (host.includes('youtube.com')) {
                        const v = urlObj.searchParams.get('v');
                        if (v) return `https://img.youtube.com/vi/${v}/hqdefault.jpg`;
                      }
                      if (host.includes('drive.google.com')) {
                        let fileId = "";
                        const parts = path.split('/').filter(Boolean);
                        for (let i = 0; i < parts.length; i++) {
                          if (parts[i] === 'd' && i + 1 < parts.length) {
                            fileId = parts[i+1];
                            break;
                          }
                        }
                        if (!fileId) {
                          const idParam = urlObj.searchParams.get('id');
                          if (idParam) fileId = idParam;
                        }
                        if (fileId) {
                          return `https://drive.google.com/thumbnail?id=${fileId}&sz=w600`;
                        }
                      }
                    } catch (e) {}
                    return 'https://cdn-icons-png.flaticon.com/512/3767/3767084.png';
                  };

                  return (
                    <button
                      key={idx}
                      onClick={() => setCurrentImageIndex(idx)}
                      className={`relative h-14 w-14 rounded-lg overflow-hidden flex-shrink-0 transition-all ${
                        currentImageIndex === idx ? 'ring-2 ring-teal-500 ring-offset-1' : 'opacity-60 hover:opacity-100'
                      }`}
                    >
                      <img
                        src={getMediaThumbnail(item)}
                        alt={`Thumb ${idx}`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.src = 'https://cdn-icons-png.flaticon.com/512/3767/3767084.png';
                        }}
                      />
                      {item.type === 'video' && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                          <svg className="w-5 h-5 text-white drop-shadow-md" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* RIGHT PANEL: Details Sidebar (35%) */}
          <div className="w-full md:w-[35%] h-full bg-white dark:bg-gray-900 border-l border-gray-100 dark:border-gray-800 flex flex-col overflow-hidden">


            {/* Header (Title & Actions) */}
            <div className="p-6 pb-4 border-b border-gray-100 dark:border-gray-800 flex justify-between items-start bg-white dark:bg-gray-900 z-10">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white leading-tight">{collaboration.title}</h2>
              <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                {isOwner && (
                  <>
                    <button 
                      onClick={() => setIsEditing(true)} 
                      className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full text-gray-500 dark:text-gray-400 transition-colors" 
                      title="Edit"
                    >
                      <PencilIcon className="h-5 w-5" />
                    </button>
                    <button 
                      onClick={handleDelete} 
                      className="p-2 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-500 dark:hover:text-red-400 rounded-full text-gray-500 dark:text-gray-400 transition-colors" 
                      title="Delete"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </>
                )}
                <button onClick={handleShare} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full text-gray-500 dark:text-gray-400 transition-colors" title="Share">
                  <ShareIcon className="h-5 w-5" />
                </button>
                <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full text-gray-500 dark:text-gray-400 transition-colors hidden md:block" title="Close">
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-8">
              {/* Description */}
              <div>
                <h3 className="text-sm font-bold text-gray-900 dark:text-white tracking-wider mb-3">About Project</h3>
                <p className="text-gray-600 dark:text-gray-400 leading-relaxed whitespace-pre-line text-sm">
                  {fullCollaboration.project_description}
                </p>
              </div>

              {/* Key Details */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-xl border border-gray-100 dark:border-gray-700">
                  <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 mb-1">
                    <MapPinIcon className="h-4 w-4" />
                    <span className="text-xs font-medium">Location</span>
                  </div>
                  <p className="text-gray-900 dark:text-white font-semibold text-sm truncate">{fullCollaboration.place}</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-xl border border-gray-100 dark:border-gray-700">
                  <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 mb-1">
                    <CalendarDaysIcon className="h-4 w-4" />
                    <span className="text-xs font-medium">Date</span>
                  </div>
                  <p className="text-gray-900 dark:text-white font-semibold text-sm truncate">
                    {new Date(fullCollaboration.start_date).toLocaleDateString()}
                  </p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-xl border border-gray-100 dark:border-gray-700">
                  <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 mb-1">
                    <ClockIcon className="h-4 w-4" />
                    <span className="text-xs font-medium">Duration</span>
                  </div>
                  <p className="text-gray-900 dark:text-white font-semibold text-sm truncate">{fullCollaboration.duration}</p>
                </div>
                {fullCollaboration.is_paid && (
                  <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-xl border border-gray-100 dark:border-gray-700">
                    <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 mb-1">
                      <CurrencyRupeeIcon className="h-4 w-4" />
                      <span className="text-xs font-medium">Budget</span>
                    </div>
                    <p className="text-gray-900 dark:text-white font-semibold text-sm truncate">{formatCurrency(fullCollaboration.budget)}</p>
                  </div>
                )}
              </div>

              {/* Tags */}
              {fullCollaboration.tags && fullCollaboration.tags.length > 0 && (
                <div>
                  <h3 className="text-sm font-bold text-gray-900 dark:text-white tracking-wider mb-3 flex items-center gap-2">
                    <TagIcon className="h-4 w-4" />
                    Tags
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {fullCollaboration.tags.map((tag, idx) => (
                      <span key={idx} className="px-3 py-1 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded-lg text-xs font-medium border border-gray-200 dark:border-gray-700">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Team */}
              <div>
                <h3 className="text-sm font-bold text-gray-900 dark:text-white tracking-wider mb-3 flex items-center gap-2">
                  <UserIcon className="h-4 w-4" />
                  The Team
                </h3>
                <div className="space-y-3">
                  {sortedCollaborators.map((collaborator) => (
                    <div 
                      key={collaborator.id}
                      onClick={() => {
                        if (collaborator.is_portal_user && collaborator.user_id && !collaborator.user?.deletion_scheduled_at) {
                          onClose();
                          navigate(`/profile/${collaborator.user_id}`);
                        }
                      }}
                      className={`flex flex-col p-3 rounded-xl border transition-all ${
                        collaborator.is_portal_user && collaborator.user_id && !collaborator.user?.deletion_scheduled_at
                          ? 'hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer border-gray-200 dark:border-gray-700' 
                          : 'border-transparent bg-gray-50 dark:bg-gray-800'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Avatar
                          src={collaborator.user?.profile_pic_thumb_signed || collaborator.user?.profile_pic || collaborator.profile_pic}
                          alt={collaborator.name}
                          size="md"
                          className="border border-white shadow-sm"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-gray-900 dark:text-white text-sm truncate">
                              {collaborator.name}
                              {collaborator.user_id === user?.id && <span className="text-gray-500 dark:text-gray-400 font-bold ml-1">(You)</span>}
                              {(!collaborator.is_portal_user || !collaborator.user_id || collaborator.user?.deletion_scheduled_at) && collaborator.user_id !== fullCollaboration.creator_id && <span className="text-gray-400 font-normal ml-1 text-xs">(External)</span>}
                            </p>
                            {(collaborator.user?.rating || 0) > 0 && (
                              <div className="flex items-center gap-1 px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded-full flex-shrink-0">
                                <StarIcon className="w-3 h-3 text-gray-400 fill-gray-400" />
                                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                  {collaborator.user?.rating?.toFixed(1)}
                                  <span className="text-gray-400 dark:text-gray-500 ml-0.5">({collaborator.user?.review_count || 0})</span>
                                </span>
                              </div>
                            )}
                          </div>
                          <p className="text-xs text-primary-600 font-medium">{collaborator.role}</p>
                        </div>
                        {isOwner && (
                          <div title={collaborator.approval_status}>
                            {collaborator.approval_status === 'approved' && <CheckCircleIcon className="h-5 w-5 text-black dark:text-white" />}
                            {collaborator.approval_status === 'rejected' && <XCircleIcon className="h-5 w-5 text-red-500" />}
                            {collaborator.approval_status === 'pending' && <ClockIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />}
                          </div>
                        )}
                      </div>

                      {/* Collaborator Insights / Message */}
                      {collaborator.insights && (
                        <div className="mt-3 pt-3 border-t border-gray-100/50 dark:border-gray-700/50">
                           <div className={`relative pl-3 border-l-2 ${collaborator.user_id === collaboration.creator_id ? 'border-gray-200 dark:border-gray-700' : 'border-gray-200 dark:border-gray-700'}`}>
                             <p className={`text-gray-600 dark:text-gray-400 text-xs italic leading-relaxed ${!showFullInsights ? 'line-clamp-2' : ''}`}>
                               "{collaborator.insights}"
                             </p>
                             {collaborator.insights.length > 100 && (
                               <button 
                                 onClick={(e) => {
                                   e.stopPropagation();
                                   setShowFullInsights(!showFullInsights);
                                 }}
                                 className={`text-[10px] font-bold ${collaborator.user_id === collaboration.creator_id ? 'text-black dark:text-white hover:text-gray-800 dark:hover:text-gray-300' : 'text-black dark:text-white hover:text-gray-800 dark:hover:text-gray-300'} mt-1 tracking-wide`}
                               >
                                 {showFullInsights ? 'Show Less' : 'Show More'}
                               </button>
                             )}
                           </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Bottom Actions (Sticky) */}
            {userCollaborator && !isOwner && userCollaborator.approval_status === 'pending' && (
              <div className="p-4 border-t border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-950">
                <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4 shadow-sm">
                  <p className="text-sm text-gray-800 dark:text-gray-200 mb-3 font-medium text-center">
                    You've been invited as <strong>{userCollaborator.role}</strong>
                  </p>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setConfirmationAction('accept')}
                      className="flex-1 py-2.5 rounded-xl bg-black dark:bg-white hover:bg-gray-800 dark:hover:bg-gray-100 text-white dark:text-black shadow-sm font-medium transition-colors"
                    >
                      Accept
                    </button>
                    <button
                      onClick={() => setConfirmationAction('reject')}
                      className="flex-1 py-2.5 rounded-xl bg-white dark:bg-gray-900 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-900/50 hover:bg-red-50 dark:hover:bg-red-900/20 font-medium transition-colors"
                    >
                      Reject
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </motion.div>

        {/* Confirmation Modal Overlay */}
        {confirmationAction && (
          <div className="absolute inset-0 z-[60] flex items-center justify-center p-4 bg-black/20 backdrop-blur-[2px]">
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md p-6 border border-gray-100 dark:border-gray-800 max-h-[90vh] overflow-y-auto">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                {confirmationAction === 'accept' ? 'Accept Collaboration' : 'Reject Collaboration'}
              </h3>
              
              {/* Review Section */}
              <CollaboratorReviewSection
                collaborators={collaboration.collaborators || []}
                reviews={reviews}
                onReviewChange={handleReviewChange}
                currentUser={user}
              />

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {confirmationAction === 'accept' ? 'Additional Message (Optional)' : 'Reason for Rejection (Required)'}
                </label>
                <textarea
                  value={responseMessage}
                  onChange={(e) => setResponseMessage(e.target.value)}
                  placeholder={confirmationAction === 'accept' ? 'Add a message...' : 'Please explain why...'}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none h-24 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                />
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setConfirmationAction(null)}
                  className="flex-1 px-4 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 font-medium transition-colors"
                  disabled={responding}
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmAction}
                  disabled={responding || (confirmationAction === 'reject' && !responseMessage.trim())}
                  className={`flex-1 px-4 py-2.5 rounded-xl text-white font-medium shadow-lg shadow-primary-500/20 transition-all ${
                    confirmationAction === 'accept'
                      ? 'bg-black dark:bg-white dark:text-black hover:bg-gray-800 dark:hover:bg-gray-100'
                      : 'bg-red-500 hover:bg-red-600'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {responding ? 'Processing...' : 'Confirm'}
                </button>
              </div>
            </div>
          </div>
        )}
      </motion.div>
      
      {/* Share Modal */}
      {showShareModal && (
        <ShareModal
            isOpen={showShareModal}
            onClose={() => setShowShareModal(false)}
            title={collaboration.title}
            url={`${window.location.origin}/profile/${collaboration.creator_id}/projects/${collaboration.id}`}
            type="collaboration"
            data={collaboration}
        />
      )}

      {/* Update Modal */}
      {isEditing && (
        <CreateCollaborationModal
          mode="update"
          collaboration={fullCollaboration}
          onClose={() => setIsEditing(false)}
          onSuccess={() => {
            setIsEditing(false);
            // Refresh details
            const fetchFullDetails = async () => {
              try {
                  const response = await collaborationAPI.getCollaboration(collaboration.id);
                  if (response.data && response.data.data) {
                      setFullCollaboration(response.data.data);
                  }
              } catch (error) {
                  console.error("Failed to fetch full collaboration details", error);
              }
            };
            fetchFullDetails();
          }}
        />
      )}

      {/* Portfolio View Modal for Image Preview */}
      {showPortfolioModal && viewablePortfolios.length > 0 && (
        <PortfolioViewModalV2
          portfolioId={viewablePortfolios[currentImageIndex]?.id || viewablePortfolios[0].id}
          onClose={() => setShowPortfolioModal(false)}
          portfolios={viewablePortfolios}
          source="profile"
          onIndexChange={(index) => setCurrentImageIndex(index)}
          isParentModal={true}
        />
      )}

      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={async () => {
          setDeleting(true);
          try {
            await collaborationAPI.deleteCollaboration(collaboration.id);
            toast.success('Project deleted successfully');
            setShowDeleteConfirm(false);
            onClose();
            // Ideally trigger a refresh in the parent component
            window.location.reload(); 
          } catch (error) {
            console.error('Failed to delete project:', error);
            toast.error('Failed to delete project');
          } finally {
            setDeleting(false);
          }
        }}
        title="Delete Project"
        message={
          <div className="space-y-3">
            <p>Are you sure you want to delete this project? This action cannot be undone.</p>
            <div className="bg-red-50 dark:bg-red-950/20 text-red-700 dark:text-red-300 p-3 rounded-xl border border-red-100 dark:border-red-900/30 text-xs font-semibold leading-relaxed">
              ⚠️ Warning: All ratings and reviews submitted for this project (including those given to participating members) will also be permanently deleted.
            </div>
          </div>
        }
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        loading={deleting}
      />
    </AnimatePresence>
  );
};

export default CollaborationShowcaseModal;
